import React, { useState, useRef, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import { useContextApi } from "../../../context/ContextApi";
import { CheckIcon, XIcon } from "../../../constants/Icons";

const OpenBalanceModal = ({
  isActive,
  onClose,
  onOBalance,
  onBalanceData,
  isBtnLoading,
}) => {
  const [amount, setAmount] = useState(
    CurrencyConverter.toCurrency(
      onBalanceData?.opening_balance || 0,
      "INR",
    ).toString(),
  );
  const { useEscapeKey } = useContextApi();
  const [errorMessage, setErrorMessage] = useState(null);
  const amountRef = useRef(null);
  const OBalanceBtnRef = useRef(null);

  const handleClose = () => {
    setAmount("");
    setErrorMessage("");
    onClose();
  };
  useEscapeKey(handleClose);
  const handleOBalance = (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    onOBalance(amount);
  };

  const validateForm = () => {
    if (!amount) {
      setErrorMessage("Amount cannot be empty.");
      return false;
    }

    const parsedValue = parseFloat(amount);
    if (isNaN(parsedValue) || parsedValue < 0) {
      setErrorMessage("Enter a valid numeric amount greater than 0.");
      return false;
    }

    const amountStr = String(amount);
    const decimalPart = amountStr.includes(".") ? amountStr.split(".")[1] : "";
    if (decimalPart && decimalPart.length !== 2) {
      setErrorMessage(
        "Amount must have exactly two decimal places (e.g., 22.22).",
      );
      return false;
    }

    setErrorMessage("");
    return true;
  };

  const handleInputChange = (value) => {
    let inputValue = value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
    const [integerPart, decimalPart] = inputValue.split(".");

    if (decimalPart && decimalPart.length > 2) {
      inputValue = `${integerPart}.${decimalPart.slice(0, 2)}`;
    }

    if (inputValue.match(/^0\d/)) {
      inputValue = inputValue.replace(/^0+/, "") || "0";
    }

    const parsedValue = parseFloat(inputValue) || 0;
    if (!inputValue || parsedValue < 0) {
      setErrorMessage("Enter a valid amount greater than 0.");
      setAmount(inputValue || "");
    } else {
      setErrorMessage("");
      setAmount(inputValue);
    }
  };

  const handleEnterFocus = (e, nextRef = null, isLast = false) => {
    if (e.key !== "Enter") return;

    e.preventDefault?.();

    const value = amount.trim();
    if (!value || parseFloat(value) <= 0) {
      setErrorMessage("Enter a valid amount greater than 0.");
      return;
    }

    if (isLast) {
      handleOBalance(e);
    } else if (nextRef?.current?.focus) {
      nextRef.current.focus();
    }
  };

  useEffect(() => {
    if (!isActive) return;

    // small timeout ensures DOM is visible after tab switch
    const timer = setTimeout(() => {
      amountRef.current?.focus();
    }, 0);

    return () => clearTimeout(timer);
  }, [isActive]);

  return (
    <div>
      <div className="modal-backdrop show"></div>
      <div
        className="modal show"
        tabIndex="-1"
        aria-modal="true"
        role="dialog"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-centered modal-sm">
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title">Opening Balance</h6>
              <button
                type="button"
                onClick={handleClose}
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="card w-100 radius-10 mb-0 shadow-none">
                <div className="card-body p-0">
                  <div className="text-center">
                    <div className="chip border-0 text-dark bg-light-blue fs-12">
                      Yesterday Closing Balance : ₹
                      {CurrencyConverter.toCurrency(
                        onBalanceData?.previous_closing_balance || 0,
                        "INR",
                      )}
                    </div>
                  </div>
                  <form className="row g-3">
                    <div className="col-md-12">
                      <label className="form-label">
                        Opening Amount <small className="text-danger">*</small>
                      </label>
                      <input
                        type="text"
                        autoComplete="off"
                        className="form-control"
                        ref={amountRef}
                        value={amount}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => handleInputChange(e.target.value)}
                        onKeyDown={(e) =>
                          handleEnterFocus(e, OBalanceBtnRef, true)
                        }
                      />
                      {errorMessage && (
                        <small className="text-danger">{errorMessage}</small>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="modal-footer d-block">
              <button
                onClick={handleClose}
                type="button"
                data-bs-dismiss="modal"
                className="btn btn-outline-secondary text-left"
              >
                <XIcon/> Close
              </button>

              {isBtnLoading ? (
                <button className="btn btn-success pb-0 text-right" disabled>
                  <CircularProgress size={22} className="text-white" />
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-success text-right"
                  onClick={handleOBalance}
                  ref={OBalanceBtnRef}
                >
                  <CheckIcon/> Save
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpenBalanceModal;
